import React from 'react';
import { Menu, Space, Typography } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { COMPETENCY_PERMS, COMPETENCY_VIEW_ANY, useCompetencyCan } from '@/hooks/useCompetency';
import { COMPETENCY_BASE } from './competencyDisplay';

const { Title } = Typography;

interface Props {
  title: React.ReactNode;
  extra?: React.ReactNode;
}

/** Page header with the module's section menu, shown on every Competency & Training page. */
const CompetencyNav: React.FC<Props> = ({ title, extra }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const P = COMPETENCY_PERMS;
  const canView = useCompetencyCan(...COMPETENCY_VIEW_ANY);
  const canTraining = useCompetencyCan(P.trainingView, P.view, P.trainingManage);
  const canAssess = useCompetencyCan(P.assessmentView, P.assessmentClose);
  const canImport = useCompetencyCan(P.import);

  const items = [
    { key: COMPETENCY_BASE, label: 'Overview', show: canView },
    { key: `${COMPETENCY_BASE}/job-profiles`, label: 'Job descriptions', show: canView },
    { key: `${COMPETENCY_BASE}/matrix`, label: 'Gap matrix', show: canView },
    { key: `${COMPETENCY_BASE}/courses`, label: 'Courses', show: canTraining },
    { key: `${COMPETENCY_BASE}/schedule`, label: 'Training schedule', show: canTraining },
    { key: `${COMPETENCY_BASE}/sessions`, label: 'Attendance', show: canTraining },
    { key: `${COMPETENCY_BASE}/effectiveness`, label: 'Effectiveness', show: true },
    { key: `${COMPETENCY_BASE}/assessments`, label: 'Assessments', show: true },
    { key: `${COMPETENCY_BASE}/cycles`, label: 'Yearly cycles', show: canAssess },
    { key: `${COMPETENCY_BASE}/import`, label: 'Import', show: canImport },
  ].filter((i) => i.show);

  const selected =
    [...items].sort((a, b) => b.key.length - a.key.length).find((i) => pathname === i.key || pathname.startsWith(`${i.key}/`))?.key ??
    COMPETENCY_BASE;

  return (
    <div style={{ marginBottom: 16 }}>
      <Menu
        mode="horizontal"
        selectedKeys={[selected]}
        items={items.map(({ key, label }) => ({ key, label }))}
        onClick={({ key }) => navigate(key)}
        style={{ marginBottom: 12 }}
      />
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          {title}
        </Title>
        {extra}
      </Space>
    </div>
  );
};

export default CompetencyNav;
